import { Alert, Button, TextField, Typography } from "@mui/material";
import Logger from "../../../services/Logger";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import React, { useState } from "react";
import ApiService from "../../../services/ApiService";

export default function HookItMain() {
    const [content, setContent] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [response, setResponse] = useState(null);

    const handleSend = async () => {
        if (!content || !content.trim()) {
            setError("Please enter content");
            return;
        }

        setLoading(true);
        setError(null);
        setResponse(null);

        try {
            // Parse content - check if it's a JSON array or just a string
            let requestBody;
            try {
                // Try to parse as JSON array first
                const parsed = JSON.parse(content.trim());
                if (Array.isArray(parsed)) {
                    requestBody = { content: parsed };
                } else {
                    // If it's a valid JSON but not an array, treat as string
                    requestBody = { content: content.trim() };
                }
            } catch (e) {
                // If parsing fails, treat as plain string
                requestBody = { content: content.trim() };
            }

            const result = await ApiService.postData("/test/hook-it", requestBody);
            
            if (result) {
                setResponse(result);
            } else {
                setError("No response received");
            }
        } catch (err) {
            Logger.error("Hook it error:", err);
            setError(err.message || "Failed to hook the content. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
            e.preventDefault();
            handleSend();
        }
    };

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, width: "100%" }}>
            <Typography variant="h6" gutterBottom>
                Hook the Word
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Enter content as a string or a JSON array of words. Press Ctrl+Enter (or Cmd+Enter) to send.
            </Typography>
            
            <TextField
                label="Content"
                variant="outlined"
                value={content}
                onChange={(e) => {
                    setContent(e.target.value);
                    setError(null);
                }}
                onKeyPress={handleKeyPress}
                fullWidth
                placeholder='Enter text or JSON array like ["word1", "word2", "word3"]'
                multiline
                rows={8}
            />
            
            <Box>
                <Button 
                    variant="contained" 
                    onClick={handleSend}
                    disabled={loading || !content.trim()}
                >
                    {loading ? "Sending..." : "Send"}
                </Button>
            </Box>
            
            {error && (
                <Alert severity="error">
                    {error}
                </Alert>
            )}
            
            {response && (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    <Paper elevation={2} sx={{ p: 2 }}>
                        <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: "bold" }}>
                            Response:
                        </Typography>
                        {response.title && (
                            <Box sx={{ mb: 2 }}>
                                <Typography variant="body2" color="text.secondary" gutterBottom>
                                    Title:
                                </Typography>
                                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                    {response.title}
                                </Typography>
                            </Box>
                        )}
                        {response.content && (
                            <Box>
                                <Typography variant="body2" color="text.secondary" gutterBottom>
                                    Content:
                                </Typography>
                                <TextField
                                    value={typeof response.content === 'string' 
                                        ? response.content 
                                        : JSON.stringify(response.content, null, 2)}
                                    fullWidth
                                    disabled
                                    multiline
                                    rows={4}
                                    variant="outlined"
                                />
                            </Box>
                        )}
                    </Paper>
                </Box>
            )}
        </Box>
    );
}

